import React, { Component } from 'react';
import CoopCursorBorder from '../../../coop-cursor-border';
import PropTypes from 'prop-types';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import SelectGraphicLine from '../../../element-types/connector-element/select-graphic-line';
import omit from 'lodash/omit';
import { omitStyle } from '@src/slide/infrastructure/module/independent/assert/get-set';
import * as commonFun from '../../../common';
import SelectedElementWrapperUtil from '../../selected-element-layer/utils';
import { GroupUIEdge } from '../../../element-types/group-element/constant';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { reaction } from 'mobx';
import { disposeOnUnmount } from 'mobx-react';
import { ServicesContext } from '../../context';
import { ICursorManager } from '@src/slide/application/service/cursor-manager/core/cursor-manager.interface';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { IConnectorStyle } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/connector-model-props.interface';

class CursorBorder extends Component<{ scale: number }, { cursorManager: ICursorManager | undefined }> {
    public static contextType = ServicesContext;

    public static propTypes = {
        scale: PropTypes.number,
    };

    declare public context: React.ContextType<typeof ServicesContext>;
    private instance?: IDynamicService<ICursorManager>;
    private cacheElements: any[];

    public constructor(props, context) {
        super(props, context);
        this.instance = this.context.cursorManager;
        this.state = {
            cursorManager: undefined,
        };
        // 缓存当前协同的元素，用来做changeSet监听
        this.cacheElements = [];
    }

    public componentDidMount() {
        document.addEventListener('mousemove', this.handleMouseMove);
        this.instance?.promise().then((cursorManager) => {
            this.setState({ cursorManager });
            disposeOnUnmount(this, reaction(
                () => cursorManager.elementsWithCursors.length,
                () => {
                    this.updateCursorElementsListener();
                    this.setState({});
                },
            ));
        });
    }

    public componentWillUnmount() {
        document.removeEventListener('mousemove', this.handleMouseMove);
        this.cacheElements.forEach((element) => {
            element?.changeSetEngine?.removeChangesetObserver(this.refreshComponent);
        });
    }

    public refreshComponent = () =>  {
        this.setState({});
    };

    // 当协同操作的元素数量更新时，则对增减的model进行事件绑定和卸载，用以监听元素移动等行为时协同框联动
    public async updateCursorElementsListener() {
        // 最新的协同操作的元素
        const latestElements = (await this.instance?.promise())?.elementsWithCursors.map(e => e.element);
        // 移除当前组件缓存的已不处于协同编辑的元素，并解除事件绑定
        for (let i = 0; i < this.cacheElements.length; i++) {
            if (!latestElements?.includes(this.cacheElements[i])) {
                const [deleteCacheElement] = this.cacheElements.splice(i, 1);
                i = i - 1;
                deleteCacheElement?.changeSetEngine?.removeChangesetObserver(this.refreshComponent);
            }
        }
        // 加入新增的协同编辑的元素，并监听changeSet变化，当元素更新时协同框实时刷新
        latestElements?.forEach((element) => {
            if (!this.cacheElements.includes(element)) {
                this.cacheElements.push(element);
                element?.changeSetEngine?.addChangesetObserver(this.refreshComponent);
            }
        });
    }

    public handleMouseMove = async (e) => {
        const { clientX, clientY } = e;
        const { elementsWithCursors } = await (this.instance?.promise()) ?? {};
        if (elementsWithCursors?.length === 0) {
            return null;
        }
        elementsWithCursors?.forEach((item) => {
            const [cursorBorder] = document.getElementsByClassName(`cursor-border-${item.id}`) as any;
            if (cursorBorder) {
                const rect = cursorBorder.getBoundingClientRect();
                const { top, left, width, height } = rect;
                if (!(clientX >= left && clientX <= left + width && clientY >= top && clientY <= top + height)) {
                    cursorBorder.style.pointerEvents = 'auto';
                }
            }
        });
    };

    public handleMouseEnter = async (element, id) => {
        const [cursorBorder] = document.getElementsByClassName(`cursor-border-${id}`) as any;
        if (cursorBorder) {
            cursorBorder.style.pointerEvents = 'none';
        }
        (await this.context.cursorManager?.promise())?.showCursorTagForElement(element);
    };

    public handleMouseLeave = async (element) => {
        (await this.context.cursorManager?.promise())?.hideCursorTagForElement(element);
    };

    // 找根组元素
    public getRootGroup = (element) => {
        let uiModel = element.parent;
        while (uiModel && !this.isRootGroup(uiModel)) {
            uiModel = uiModel.parent;
        }
        return uiModel;
    };

    public isRootGroup(uiModel) {
        return uiModel.type === UIModelType.GROUP && uiModel.isRootGroup;
    }

    public getCoopCursorBorder = (item) => {
        const elementModel = item.element;
        const { width, height } = elementModel.props.style;
        const isRootGroup = this.isRootGroup(elementModel);

        const innerRect = {
            width,
            height,
        };

        if (isRootGroup) {
            innerRect.width = width + (GroupUIEdge * 2);
            innerRect.height = height + (GroupUIEdge * 2);
        }

        return (
            <CoopCursorBorder
                size={innerRect}
                color={item.cursor[0].color}
                shouldRoundSize={elementModel.type === UIModelType.SHAPE
                || elementModel.type === UIModelType.IMAGE}
            />
        );
    };

    public getSelectGraphicLine = (item, frame) => (
        <SelectGraphicLine
            model={{
                ...omit(omitStyle(item.element.props.style) as IConnectorStyle, 'width', 'height'),
                width: frame.width,
                height: frame.height,
            }}
            strokeColor={item.cursor[0].color}
            strokeOpacity={0.7}
        />
    );

    public renderCursorBorder = (item) => {
        const elementModel = item.element;
        if (!this.state.cursorManager?.isElementInSlide(elementModel)) {
            return null;
        }

        let frameInfoForOuter = SelectedElementWrapperUtil.frameInfoForOuter(elementModel, true);
        const isRootGroup = this.isRootGroup(elementModel);
        let rootGroupFrame = {};
        if (isRootGroup) {
            const rect = elementModel.getFrame?.().getFrameInfo(!!elementModel.operationFrame);
            rootGroupFrame = {
                left: rect.left - GroupUIEdge,
                top: rect.top - GroupUIEdge,
                width: rect.width + (GroupUIEdge * 2),
                height: rect.height + (GroupUIEdge * 2),
            };
        }
        if (elementModel.type === UIModelType.TABLE) {
            if (this.context.editingStateManager?.isResizing) {
                frameInfoForOuter = elementModel.getFrame?.().getFrameInfo(false);
            }
        }
        let transformInfo = SelectedElementWrapperUtil.transformForOuter(elementModel);
        if (elementModel.type === UIModelType.CONNECTOR) {
            transformInfo = commonFun.transformOfRotateAndFlip(frameInfoForOuter);
        }

        const flag = browser.isMobile ? 'none' : 'auto';

        return (
            !elementModel.selected ? <div
                className={`cursor-border-${item.id}`}
                key={item.id}
                style={{
                    ...frameInfoForOuter,
                    ...rootGroupFrame,
                    position: 'absolute',
                    transform: transformInfo,
                    pointerEvents: flag,
                } as any}
                onMouseEnter={() => this.handleMouseEnter(item.element, item.id)}
                onMouseLeave={() => this.handleMouseLeave(item.element)}
            >
                {
                    item.element.type === UIModelType.CONNECTOR
                        ? this.getSelectGraphicLine(item, frameInfoForOuter)
                        : this.getCoopCursorBorder(item)
                }
            </div> : null
        );
    };

    public render() {
        const elementsWithCursors = this.state.cursorManager?.elementsWithCursors;
        if (elementsWithCursors?.length === 0) {
            return null;
        }

        return (
            <React.Fragment>
                {
                    elementsWithCursors?.map(item => this.renderCursorBorder(item))
                }
            </React.Fragment>
        );
    }
}

export default CursorBorder;
